import { useEffect, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import KeyIcon from "@mui/icons-material/VpnKey";
import { useApiClient } from "../api/ApiClientContext";
import { ApiError } from "../api/types";
import { useAuth } from "../context/AuthContext";
import { LabeledTextField } from "../components/LabeledTextField";
import { LegalNoticeDialog } from "../components/LegalNoticeDialog";
import { LogoMark } from "../components/LogoMark";
import { PasswordField } from "../components/PasswordField";
import { colorTokens, radiusTokens } from "../theme/tokens";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { APP_VERSION } from "../version";

/**
 * Same generic message for any invalid credential (docs/api/portal-api.yaml
 * only ever returns "invalid_credentials" for a wrong ID or password on
 * this route): the login page must not reveal which part failed.
 */
const GENERIC_LOGIN_ERROR = "We could not sign you in. Check your ID and password and try again.";

/**
 * Friendly copy for /login?error=<code>, set by the BFF's OIDC callback
 * (GET /api/portal/oidc/callback) on a full-page redirect back here. The
 * raw code is never rendered: an unrecognised one falls back to the same
 * generic message as `oidc_error`.
 */
const OIDC_ERROR_MESSAGES: Record<string, string> = {
  oidc_state: "Your sign-in session expired, please try again.",
  oidc_denied: "Sign-in was cancelled.",
  not_staff: "Your account is not authorised for staff access.",
  oidc_error: "Sign-in failed, please try again.",
};

function oidcErrorMessage(code: string): string {
  return OIDC_ERROR_MESSAGES[code] ?? OIDC_ERROR_MESSAGES.oidc_error;
}

export function LoginPage() {
  const apiClient = useApiClient();
  const { me, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // The staff/partner toggle lives in the URL (/login vs /login/staff), the
  // same "single source of truth" reasoning as PartnerFilesPage's ?tab=:
  // a direct navigation or a refresh must land in the right mode. An OIDC
  // callback error always implies staff mode, even though the BFF redirects
  // it to plain /login (task requirement: "put the staff mode forward when
  // the error comes from OIDC").
  const searchParams = new URLSearchParams(location.search);
  const oidcErrorCode = searchParams.get("error");
  const asStaff = location.pathname === "/login/staff" || oidcErrorCode !== null;
  // Anonymous included (docs/api/portal-api.yaml): GET /session/me always
  // carries staffAuth, so AuthContext's own initial load already has it by
  // the time this page is interactive. "local" is this client's own
  // default while that first load is still in flight or on an older BFF
  // that does not send the field yet.
  const staffAuth = me?.staffAuth ?? "local";
  const oidcMode = asStaff && staffAuth === "oidc";

  const [id, setId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(oidcErrorCode ? oidcErrorMessage(oidcErrorCode) : null);
  const [submitting, setSubmitting] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);

  useDocumentTitle("Leeft MFT - Sign in");

  const idError = error !== null && id.trim() === "";
  const passwordError = error !== null && password === "";

  function handleModeChange(nextAsStaff: boolean) {
    setError(null);
    navigate(nextAsStaff ? "/login/staff" : "/login", { replace: true });
  }

  function handleStartOidc() {
    // A full-page navigation, not fetch: this leaves the SPA entirely for
    // the corporate IdP's own sign-in pages, per ADR 0008 section 2.
    window.location.assign("/api/portal/oidc/start");
  }

  // Manager decision 2026-09-11: /login/staff must not stop at an
  // intermediate "Sign in with Leeft SSO" screen when this deployment uses
  // OIDC (production always does: config.go refuses PORTAL_STAFF_AUTH=local
  // in ECS) - it goes straight to the IdP, the same "no intermediate form"
  // rule the partner form's own SSO button already follows
  // (handleSsoButtonClick). Skipped when an OIDC callback error just sent
  // the user back here (asStaff is forced true in that case too): the
  // error must stay readable, not be immediately redirected away from.
  useEffect(() => {
    if (oidcMode && !error) {
      window.location.assign("/api/portal/oidc/start");
    }
  }, [oidcMode, error]);

  /**
   * The partner form's "Sign in with Leeft SSO" button (manager decision,
   * 2026-09-11, replacing the old toggle link): when this deployment has
   * OIDC wired up (`staffAuth === "oidc"`), it goes straight to the IdP,
   * no intermediate toggle or form. On the local stack, before SSO is
   * wired (`staffAuth === "local"`), it falls back to switching to the
   * existing staff local form at /login/staff, unchanged.
   */
  function handleSsoButtonClick() {
    if (staffAuth === "oidc") {
      handleStartOidc();
    } else {
      handleModeChange(true);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (id.trim() === "" || password === "") {
      setError(asStaff ? "Enter your username and password." : "Enter your ID and password.");
      return;
    }

    setSubmitting(true);
    try {
      const login = asStaff ? apiClient.loginStaff : apiClient.login;
      await login({ id: id.trim(), password });
      await refresh();
      navigate("/", { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === "password_change_required") {
        // Partners are not forced to change their password at first
        // sign-in; this only fires when staff set the flag directly in
        // WebAdmin (ADR 0008 section 2). The session cookie is set even
        // though this call itself 403s. RequireAuth sends the user on to
        // /change-password once the refreshed session shows
        // mustChangePassword.
        await refresh();
        navigate("/", { replace: true });
        return;
      }
      if (caught instanceof ApiError && caught.code === "invalid_credentials") {
        setError(GENERIC_LOGIN_ERROR);
      } else if (caught instanceof ApiError && (caught.code === "not_staff" || caught.code === "local_staff_auth_disabled")) {
        setError(caught.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        bgcolor: colorTokens.bg,
      }}
    >
      <Box
        component="main"
        sx={{
          flex: "1 1 480px",
          // Flex items default to min-width: auto, which stops a row flex
          // child from shrinking below its content's intrinsic width and
          // causes horizontal overflow on narrow screens (the classic
          // flexbox min-width bug). minWidth: 0 lets this column shrink
          // to the viewport instead.
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          px: { xs: 3, sm: 8 },
          py: { xs: 4, sm: 8 },
        }}
      >
        <LogoMark size={56} />

        <Box sx={{ maxWidth: { xs: "100%", sm: 400 }, width: "100%", mx: { xs: 0, md: "auto" } }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }} gutterBottom>
            {asStaff ? "Sign in to Staff Access" : "Sign in to Partner Access"}
          </Typography>

          {oidcMode ? (
            <Stack spacing={3} sx={{ mt: 4 }}>
              {error && <Alert severity="error">{error}</Alert>}

              <Button variant="contained" size="large" fullWidth onClick={handleStartOidc}>
                Sign in with Leeft SSO
              </Button>

              <Typography variant="body2" align="center">
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => handleModeChange(false)}
                >
                  Sign in as a partner instead
                </Link>
              </Typography>
            </Stack>
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 4 }}>
              <Stack spacing={3}>
                {error && <Alert severity="error">{error}</Alert>}

                <LabeledTextField
                  id="login-id"
                  label={asStaff ? "Staff username" : "User ID"}
                  autoComplete="username"
                  fullWidth
                  value={id}
                  onChange={(event) => setId(event.target.value)}
                  error={idError}
                />

                <PasswordField
                  id="password"
                  label="Password"
                  autoComplete="current-password"
                  fullWidth
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  error={passwordError}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={submitting}
                >
                  {submitting ? "Signing in..." : "Sign in"}
                </Button>

                {/*
                  No email and no self-service password reset (ADR 0008
                  section 7): a forgotten password is reset by staff in the
                  portal, not by the account owner. Plain text on purpose,
                  not a "Forgot password?" link.
                */}
                <Typography variant="body2" color="text.secondary" align="center">
                  {asStaff ? (
                    <>
                      Forgot your password? Use your usual staff account recovery.{" "}
                    </>
                  ) : (
                    "Forgot your password? Contact your Leeft contact."
                  )}
                </Typography>

                {asStaff ? (
                  <Typography variant="body2" align="center">
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => handleModeChange(false)}
                    >
                      Sign in as a partner instead
                    </Link>
                  </Typography>
                ) : (
                  <>
                    <Divider sx={{ color: "text.secondary" }}>or</Divider>
                    <Button
                      variant="outlined"
                      size="large"
                      fullWidth
                      startIcon={<KeyIcon fontSize="small" />}
                      onClick={handleSsoButtonClick}
                      sx={{ borderRadius: radiusTokens.pill }}
                    >
                      Sign in with Leeft SSO
                    </Button>
                  </>
                )}
              </Stack>
            </Box>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary">
          <Link
            component="button"
            type="button"
            underline="hover"
            color="inherit"
            onClick={() => setLegalOpen(true)}
          >
            Legal notices and source code
          </Link>
          {" · "}
          Version {APP_VERSION}
        </Typography>
        <LegalNoticeDialog open={legalOpen} onClose={() => setLegalOpen(false)} />
      </Box>

      <Box
        component="aside"
        aria-hidden="true"
        sx={{
          display: { xs: "none", md: "block" },
          flex: "1 1 640px",
          p: { md: 3, lg: 4 },
        }}
      >
        <Box
          sx={{
            height: "100%",
            borderRadius: "32px",
            position: "relative",
            overflow: "hidden",
            background: `radial-gradient(circle at 20% 10%, ${colorTokens.brand} 0%, #0B2A2D 55%, #061618 100%)`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: 6,
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              backgroundImage:
                "radial-gradient(rgba(255,255,255,0.16) 1px, transparent 1px)",
              backgroundSize: "18px 18px",
              opacity: 0.5,
            },
          }}
        >
          {/* Proposed copy, needs Leeft marketing sign-off before release. */}
          <Typography
            variant="h3"
            component="p"
            sx={{
              fontWeight: 700,
              position: "relative",
              maxWidth: 420,
              color: colorTokens.onBrand,
            }}
          >
            One access, all your files.
          </Typography>

          <Stack
            direction="row"
            spacing={1.5}
            sx={{ position: "relative", alignItems: "center" }}
          >
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.72)" }}>
              Secure managed file transfer for
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
              <LogoMark size={18} variant="onDark" />
              {/*
                There is no separate white Leeft wordmark asset in the
                repo (only the mark PNG, docs/design/leeft-design-tokens.md
                section 2.2 lists a wordmark SVG that was fetched from the
                public site, not committed here). Rendered as text next to
                the mark until that asset is provided.
              */}
              <Typography variant="body2" sx={{ fontWeight: 700, color: colorTokens.onBrand }}>
                Leeft
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
